import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, ShieldX, Loader2, CheckCircle2, XCircle,
  Pencil, AlertTriangle, Pill, FlaskConical, Save, ExternalLink, ListChecks,
  ChevronRight, SkipForward, Link2, History, PlusCircle, TrendingUp, Bell,
  CalendarRange, Trash2, CheckSquare, Square, Boxes, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchVerificationQueue,
  verifyInteraction,
  rejectInteraction,
  editAndVerifyInteraction,
  spotCheckInteraction,
  fetchVerificationHistory,
  fetchCoverageGaps,
  createCoverageRow,
  fetchGapDemand,
  fetchVerificationWeekPlan,
  rejectInteractionBulk,
  addIngredientAlias,
} from "../lib/api";
import { useAdminAuth } from "../lib/adminAuth";

// Preset rejection reasons — one click rejects with the chosen reason, logged
// to the interaction audit (same path as a typed reason).
const REJECT_PRESETS = [
  "No label or guideline support",
  "Duplicate of another row",
  "Not an active ingredient",
  "Out of scope",
];

// Clinical severity taxonomy, highest-risk first (mirrors backend _SEVERITY_ORDER).
const SEVERITY_ORDER = [
  "CONTRAINDICATED",
  "HIGH_IRRITATION_RISK",
  "WARNING",
  "DUPLICATE_ACTIVE",
  "CAUTION",
  "INFO",
];

const SEV_TONE = {
  CONTRAINDICATED:      "bg-red-100 border-red-300 text-red-900",
  HIGH_IRRITATION_RISK: "bg-orange-100 border-orange-300 text-orange-900",
  WARNING:              "bg-amber-100 border-amber-300 text-amber-900",
  DUPLICATE_ACTIVE:     "bg-violet-100 border-violet-300 text-violet-900",
  CAUTION:              "bg-yellow-50 border-yellow-200 text-yellow-800",
  INFO:                 "bg-sky-50 border-sky-200 text-sky-800",
};

const SEV_LABEL = {
  CONTRAINDICATED:      "Contraindicated",
  HIGH_IRRITATION_RISK: "High irritation risk",
  WARNING:              "Warning",
  DUPLICATE_ACTIVE:     "Duplicate active",
  CAUTION:              "Caution",
  INFO:                 "Info",
};

const EVIDENCE_LEVELS = ["STRONG", "MODERATE", "THEORETICAL"];

// UI mirror of services/source_config.py INTERNAL_SOURCE_LABEL_PATTERNS —
// hint only; the backend enforces authoritatively on verify.
const INTERNAL_LABEL_RE =
  /(catalog expansion|medskin clinical curation|medication schema expansion|dermatology practice|medskin internal|fix pass|coverage-fix|pre-launch|mock_data|batch import)/i;

function urlAllowed(u, domains) {
  if (!u || typeof u !== "string") return false;
  try {
    const url = new URL(u);
    if (url.protocol !== "https:") return false;
    const h = url.hostname.toLowerCase();
    return domains.some((d) => h === d || h.endsWith("." + d));
  } catch {
    return false;
  }
}

// Returns list of unmet requirements (empty = verify allowed).
function sourceGateProblems({ sourceType, url, tbTitle, tbPage, evidence, sourceName }, domains) {
  const problems = [];
  if (!EVIDENCE_LEVELS.includes((evidence || "").toUpperCase()))
    problems.push("set evidence level (STRONG / MODERATE / THEORETICAL)");
  if (sourceType === "textbook") {
    if (!(tbTitle || "").trim()) problems.push("enter the textbook title");
    if (!String(tbPage || "").trim()) problems.push("enter the textbook page");
  } else if (!urlAllowed(url, domains)) {
    problems.push("add an https source URL on an allowed domain");
  }
  if (INTERNAL_LABEL_RE.test(sourceName || ""))
    problems.push("replace the internal batch label with the real source name");
  return problems;
}

function dailymedLabelUrl(setid) {
  return setid
    ? `https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=${encodeURIComponent(setid)}`
    : null;
}

// Opens the EXACT stored DailyMed label for the medication (never a search).
// Shows "No label set" when the medication has no label_setid.
function DailyMedChip({ setid, testid, small }) {
  const url = dailymedLabelUrl(setid);
  const base = small
    ? "ml-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold align-middle"
    : "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold";
  if (!url) {
    return (
      <span
        data-testid={testid}
        className={`${base} bg-stone-100 border border-stone-200 text-stone-400`}
        title="No DailyMed label is stored for this medication"
      >
        No label set
      </span>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className={`${base} bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100`}
      title="Open the exact DailyMed label for this medication (new tab)"
    >
      DailyMed <ExternalLink className={small ? "h-2 w-2" : "h-2.5 w-2.5"} />
    </a>
  );
}

function SevPill({ severity }) {
  const s = (severity || "").toUpperCase();
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${SEV_TONE[s] || "bg-stone-50 border-stone-200 text-stone-700"}`}>
      {(s === "CONTRAINDICATED" || s === "HIGH_IRRITATION_RISK") && <AlertTriangle className="h-3 w-3" />}
      {SEV_LABEL[s] || severity || "—"}
    </span>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return String(v).slice(0, 10); }
}

function StatTile({ label, value, tone = "stone", testid }) {
  const TONES = {
    stone:  "bg-stone-50 border-stone-200 text-stone-900",
    amber:  "bg-amber-50 border-amber-200 text-amber-900",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-900",
    red:    "bg-red-50 border-red-200 text-red-900",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${TONES[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function FieldRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-44 text-stone-500 text-xs uppercase tracking-wider font-semibold pt-0.5 shrink-0">{label}</span>
      <span className="text-stone-800 flex-1">{value}</span>
    </div>
  );
}

function SpotCheckPanel({ item }) {
  const existing = item.spot_checked_by || null;
  const [name, setName] = useState(existing?.name || "");
  const [date, setDate] = useState(existing?.date || new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(existing?.result || "Pass");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(existing);

  const save = async () => {
    if (!name.trim()) { toast.error("Reviewer name is required."); return; }
    setBusy(true);
    try {
      const r = await spotCheckInteraction(item.id, { name: name.trim(), role: "PharmD", date, result });
      setSaved(r.spot_checked_by);
      toast.success("Spot-check saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spot-check failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-stone-100 bg-amber-50/40 px-5 py-4" data-testid={`spotcheck-panel-${item.id}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Pharmacist spot-check</p>
      {saved && (
        <p className="mt-1 text-xs text-stone-600" data-testid="spotcheck-current">
          {saved.result === "Pass"
            ? `Shows publicly: "Spot-checked by ${saved.name}, ${saved.role || "PharmD"} · ${saved.date}"`
            : `Recorded: ${saved.result} by ${saved.name} · ${saved.date} (not shown publicly)`}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-end gap-2 text-sm">
        <label className="space-y-1">
          <span className="block text-[10px] text-stone-500">Reviewer name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="spotcheck-name"
            className="h-9 px-2 rounded border border-stone-300 bg-white text-sm" />
        </label>
        <label className="space-y-1">
          <span className="block text-[10px] text-stone-500">Role</span>
          <input value="PharmD" disabled data-testid="spotcheck-role"
            className="h-9 w-20 px-2 rounded border border-stone-300 bg-stone-100 text-sm" />
        </label>
        <label className="space-y-1">
          <span className="block text-[10px] text-stone-500">Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="spotcheck-date"
            className="h-9 px-2 rounded border border-stone-300 bg-white text-sm" />
        </label>
        <label className="space-y-1">
          <span className="block text-[10px] text-stone-500">Result</span>
          <select value={result} onChange={(e) => setResult(e.target.value)} data-testid="spotcheck-result"
            className="h-9 px-2 rounded border border-stone-300 bg-white text-sm">
            <option>Pass</option><option>Change</option><option>Reject</option>
          </select>
        </label>
        <Button size="sm" onClick={save} disabled={busy} data-testid="spotcheck-save">
          {busy ? "Saving…" : "Save spot-check"}
        </Button>
      </div>
    </div>
  );
}

function VerifyCard({ item, allowedDomains, waiting = 0, skipLabel = "Next unverified", onVerified, onRejected, onSkip, hasNext }) {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState(null);
  useEffect(() => {
    if (!historyOpen || historyEvents !== null) return;
    let cancelled = false;
    fetchVerificationHistory(item.id)
      .then((d) => { if (!cancelled) setHistoryEvents(d?.events || []); })
      .catch(() => { if (!cancelled) setHistoryEvents([]); });
    return () => { cancelled = true; };
  }, [historyOpen, historyEvents, item.id]);
  const [edits, setEdits] = useState({
    severity: item.severity || "WARNING",
    risk_level: item.risk_level || "MODERATE",
    interaction_type: item.interaction_type || "USE_WITH_CAUTION",
    what_happens_plain: item.what_happens_plain || "",
    what_happens_scientific: item.what_happens_scientific || "",
    recommendation: item.recommendation || "",
    source_primary: item.source_primary || "",
    source_primary_url: item.source_primary_url || "",
    confidence_level: item.confidence_level || "TEXTBOOK",
    evidence_level: item.evidence_level || "",
    source_type: item.source_type || "url",
    textbook_title: item.textbook_title || "",
    textbook_page: item.textbook_page || "",
    show_to_users: item.show_to_users !== false,
  });

  // Gate on the ROW as-is (plain Verify) and on the EDITS (Save & verify).
  const rowProblems = sourceGateProblems({
    sourceType: item.source_type || "url",
    url: item.source_primary_url,
    tbTitle: item.textbook_title,
    tbPage: item.textbook_page,
    evidence: item.evidence_level,
    sourceName: item.source_primary,
  }, allowedDomains);
  const editProblems = sourceGateProblems({
    sourceType: edits.source_type,
    url: edits.source_primary_url,
    tbTitle: edits.textbook_title,
    tbPage: edits.textbook_page,
    evidence: edits.evidence_level,
    sourceName: edits.source_type === "textbook" ? edits.textbook_title : edits.source_primary,
  }, allowedDomains);

  const handleVerify = async () => {
    setBusy(true);
    try {
      await verifyInteraction(item.id, { notes });
      toast.success("Interaction verified");
      onVerified(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setBusy(false); }
  };

  const handleEditAndVerify = async () => {
    setBusy(true);
    try {
      await editAndVerifyInteraction(item.id, { ...edits, notes });
      toast.success("Saved & verified");
      onVerified(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const handleReject = async (presetReason) => {
    const finalReason = (presetReason || reason).trim();
    if (!finalReason) { toast.error("Reason is required to reject."); return; }
    setBusy(true);
    try {
      await rejectInteraction(item.id, finalReason);
      toast.success("Interaction rejected");
      onRejected(item.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  const med = item.medication || {};
  const ing = item.ingredient || {};
  const medName = med.generic_name || item.medication_id || "";

  return (
    <article data-testid={`verify-card-${item.id}`} className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-100 bg-gradient-to-br from-stone-50 to-white flex-wrap">
        <SevPill severity={item.severity} />
        <div className="flex items-center gap-2 text-sm text-stone-700">
          <Pill className="h-4 w-4 text-stone-400" />
          <span className="font-semibold">{med.generic_name || item.medication_id?.slice(0, 8)}</span>
          <DailyMedChip setid={med.label_setid} testid={`dailymed-link-card-${item.id}`} />
          <span className="text-stone-400">×</span>
          <FlaskConical className="h-4 w-4 text-stone-400" />
          <span className="font-semibold">{ing.name || item.ingredient_id?.slice(0, 8)}</span>
        </div>
        {waiting > 0 && (
          <span
            data-testid={`gap-waiting-card-${item.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-bold text-amber-900"
            title={`${waiting} ${waiting === 1 ? "person is" : "people are"} waiting to be emailed when this is verified`}
          >
            <Bell className="h-3 w-3" /> {waiting} waiting
          </span>
        )}
        <span className="ml-auto text-[11px] text-stone-400">Added {fmtDate(item.created_at)}</span>
      </div>

      {/* Content — the claim */}
      <div className="px-5 py-4 space-y-2.5">
        <FieldRow label="Risk level" value={item.risk_level} />
        <FieldRow label="Plain explanation" value={item.what_happens_plain} />
        <p className="text-[11px] text-stone-400 pl-44 -mt-1" data-testid="verify-explanation-helper">
          Describe what the label says. Do not write "stop", "do not use" or any instruction.
        </p>
        <FieldRow label="Scientific" value={item.what_happens_scientific} />
        <FieldRow label="Recommendation" value={item.recommendation} />
        <FieldRow
          label="Existing source"
          value={
            item.source_primary_url ? (
              <a className="text-blue-600 hover:underline inline-flex items-center gap-1" href={item.source_primary_url} target="_blank" rel="noreferrer">
                {item.source_primary || item.source_primary_url}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (item.source_primary || <span className="text-stone-400 italic">none on file</span>)
          }
        />
        <FieldRow label="Evidence level" value={item.evidence_level || <span className="text-stone-400 italic">not set</span>} />
        <FieldRow label="Confidence" value={item.confidence_level} />
      </div>

      <SpotCheckPanel item={item} />

      {/* Edit form */}
      {editing && (
        <div className="border-t border-stone-100 bg-stone-50 px-5 py-4 grid sm:grid-cols-2 gap-3 text-sm" data-testid={`verify-edit-form-${item.id}`}>
          <label className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Severity</span>
            <select
              value={edits.severity}
              onChange={(e) => setEdits({ ...edits, severity: e.target.value })}
              className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              data-testid="verify-edit-severity"
            >
              {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{SEV_LABEL[s]}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Risk level</span>
            <select
              value={edits.risk_level}
              onChange={(e) => setEdits({ ...edits, risk_level: e.target.value })}
              className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              data-testid="verify-edit-risk"
            >
              <option>HIGH</option><option>MODERATE</option><option>LOW</option>
            </select>
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Plain explanation</span>
            <textarea value={edits.what_happens_plain} onChange={(e) => setEdits({ ...edits, what_happens_plain: e.target.value })} rows={2}
              data-testid="verify-edit-plain"
              className="w-full px-2 py-1.5 rounded border border-stone-300 bg-white text-sm" />
            <span className="block text-[11px] text-stone-400" data-testid="verify-edit-plain-helper">
              Describe what the label says. Do not write "stop", "do not use" or any instruction.
            </span>
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Scientific</span>
            <textarea value={edits.what_happens_scientific} onChange={(e) => setEdits({ ...edits, what_happens_scientific: e.target.value })} rows={2}
              data-testid="verify-edit-scientific"
              className="w-full px-2 py-1.5 rounded border border-stone-300 bg-white text-sm" />
            <span className="block text-[11px] text-stone-400" data-testid="verify-edit-scientific-helper">
              Describe what the label says. Do not write "stop", "do not use" or any instruction.
            </span>
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Recommendation</span>
            <textarea value={edits.recommendation} onChange={(e) => setEdits({ ...edits, recommendation: e.target.value })} rows={2}
              className="w-full px-2 py-1.5 rounded border border-stone-300 bg-white text-sm" />
            <span className="block text-[11px] text-stone-400" data-testid="verify-recommendation-helper">
              Describe what the label says. Do not write "stop", "do not use" or any instruction.
            </span>
          </label>

          {/* ── Source (required to verify) ── */}
          <div className="space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
              Source * <span className="normal-case font-normal text-stone-400">(required to verify)</span>
            </span>
            <div className="flex gap-2 pt-0.5" data-testid="verify-edit-source-type">
              {[
                ["url", "Web link"],
                ["textbook", "Textbook"],
              ].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setEdits({ ...edits, source_type: val })}
                  data-testid={`verify-edit-source-type-${val}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${edits.source_type === val ? "bg-stone-900 text-white" : "bg-white border border-stone-300 text-stone-600 hover:bg-stone-100"}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {edits.source_type === "textbook" ? (
            <>
              <label className="space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Textbook title *</span>
                <input value={edits.textbook_title} onChange={(e) => setEdits({ ...edits, textbook_title: e.target.value })}
                  placeholder="Rook's Textbook of Dermatology"
                  className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
                  data-testid="verify-edit-textbook-title" />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Page *</span>
                <input value={edits.textbook_page} onChange={(e) => setEdits({ ...edits, textbook_page: e.target.value })}
                  placeholder="p. 512"
                  className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
                  data-testid="verify-edit-textbook-page" />
              </label>
            </>
          ) : (
            <>
              <label className="space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Source name *</span>
                <input value={edits.source_primary} onChange={(e) => setEdits({ ...edits, source_primary: e.target.value })}
                  placeholder="DailyMed: Tretinoin cream — Drug Interactions"
                  className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
                  data-testid="verify-edit-source-name" />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                  Source URL *{" "}
                  {med.label_setid ? (
                    <a href={dailymedLabelUrl(med.label_setid)} target="_blank" rel="noreferrer"
                       data-testid="verify-edit-dailymed-quicklink"
                       className="normal-case font-normal text-sky-700 hover:underline">
                      open DailyMed label ↗
                    </a>
                  ) : (
                    <span data-testid="verify-edit-dailymed-quicklink"
                          className="normal-case font-normal text-stone-400"
                          title="No DailyMed label is stored for this medication">
                      No label set
                    </span>
                  )}
                </span>
                <input value={edits.source_primary_url} onChange={(e) => setEdits({ ...edits, source_primary_url: e.target.value })}
                  className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm" placeholder="https://dailymed.nlm.nih.gov/…"
                  data-testid="verify-edit-source-url" />
                <span className="block text-[10px] text-stone-400">
                  https only · {allowedDomains.join(", ")}
                </span>
              </label>
            </>
          )}

          <label className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Evidence level *</span>
            <select value={edits.evidence_level} onChange={(e) => setEdits({ ...edits, evidence_level: e.target.value })}
              className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm"
              data-testid="verify-edit-evidence">
              <option value="">— choose —</option>
              {EVIDENCE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Confidence</span>
            <select value={edits.confidence_level} onChange={(e) => setEdits({ ...edits, confidence_level: e.target.value })}
              className="w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm">
              <option>FDA_DOCUMENTED</option><option>PEER_REVIEWED</option><option>TEXTBOOK</option>
              <option>EXPERT_CONSENSUS</option><option>THEORETICAL</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-stone-700 self-end">
            <input type="checkbox" checked={edits.show_to_users} onChange={(e) => setEdits({ ...edits, show_to_users: e.target.checked })} className="h-4 w-4" />
            Show to users
          </label>

          {editProblems.length > 0 && (
            <p className="sm:col-span-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="verify-gate-hint">
              To verify: {editProblems.join(" · ")}
            </p>
          )}
        </div>
      )}

      {/* Reject reason */}
      {showReject && (
        <div className="border-t border-stone-100 bg-red-50 px-5 py-3" data-testid={`verify-reject-form-${item.id}`}>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Why is this being rejected?</span>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`verify-reject-presets-${item.id}`}>
            {REJECT_PRESETS.map((p) => (
              <button
                key={p}
                disabled={busy || !canReview}
                onClick={() => handleReject(p)}
                data-testid={`verify-reject-preset-${item.id}-${p.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                className="rounded-full border border-red-300 bg-white px-2.5 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                {p}
              </button>
            ))}
          </div>
          <label className="mt-2 block">
            <span className="text-[10px] uppercase tracking-wider text-red-600">Or type a reason</span>
            <input data-testid={`verify-reject-reason-${item.id}`} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Not supported by current FDA labeling…"
              className="mt-1 w-full h-10 px-3 rounded border border-red-200 bg-white text-sm focus:border-red-400 focus:outline-none" />
          </label>
        </div>
      )}

      {/* Notes */}
      {!showReject && (
        <div className="border-t border-stone-100 bg-stone-50 px-5 py-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Verification notes (optional)</span>
            <input data-testid={`verify-notes-${item.id}`} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Cross-checked against DailyMed…"
              className="mt-1 w-full h-9 px-2 rounded border border-stone-300 bg-white text-sm" />
          </label>
        </div>
      )}

      {/* History — read-only audit trail from interaction_review_audit */}
      <div className="border-t border-stone-100 px-5 py-3">
        <button
          type="button"
          onClick={() => setHistoryOpen((o) => !o)}
          data-testid={`verify-history-toggle-${item.id}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500 hover:text-stone-800"
        >
          <History className="h-3.5 w-3.5" /> History {historyOpen ? "▲" : "▼"}
        </button>
        {historyOpen && (
          <div className="mt-2" data-testid={`verify-history-panel-${item.id}`}>
            {historyEvents === null ? (
              <p className="text-xs text-stone-400">Loading…</p>
            ) : historyEvents.length === 0 ? (
              <p className="text-xs text-stone-400 italic">No recorded review events for this row yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {historyEvents.map((ev) => (
                  <li key={ev.id} data-testid={`verify-history-event-${ev.id}`} className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-[11px] text-stone-700">
                    <span className="font-semibold text-stone-900">{ev.action}</span>
                    {" · "}{ev.actor || "unknown"}
                    {" · "}{fmtDate(ev.timestamp)}
                    {(ev.after?.severity || ev.before?.severity) && ev.action === "severity_change" && (
                      <span className="text-stone-500"> · {ev.before?.severity} → {ev.after?.severity}</span>
                    )}
                    {ev.source_url && (
                      <span className="text-stone-500"> · <a className="text-sky-700 hover:underline" href={ev.source_url} target="_blank" rel="noreferrer">source ↗</a></span>
                    )}
                    {ev.evidence_level && <span className="text-stone-500"> · {ev.evidence_level}</span>}
                    {ev.notes && <div className="mt-0.5 text-stone-500 italic">{ev.notes}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 bg-white flex-wrap">
        {!canReview && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500" data-testid="verify-clinician-only">
            Clinical reviewer only
          </span>
        )}
        {!showReject && !editing && (
          <>
            <Button
              onClick={handleVerify}
              disabled={busy || rowProblems.length > 0 || !canReview}
              title={!canReview ? "Clinical reviewer only." : (rowProblems.length ? `To verify: ${rowProblems.join("; ")}` : "Mark as verified")}
              data-testid={`verify-btn-${item.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Verify
            </Button>
            <Button onClick={() => setEditing(true)} variant="outline" data-testid={`edit-btn-${item.id}`}>
              <Pencil className="h-4 w-4 mr-1.5" /> Edit / add source
            </Button>
            <Button onClick={() => setShowReject(true)} variant="ghost" data-testid={`reject-open-btn-${item.id}`} className="text-red-600 hover:bg-red-50">
              <XCircle className="h-4 w-4 mr-1.5" /> Reject
            </Button>
            {rowProblems.length > 0 && (
              <span className="text-[11px] text-amber-700" data-testid="verify-locked-hint">
                <Link2 className="inline h-3 w-3 mr-0.5 -mt-0.5" />
                Verify locked — source + evidence required
              </span>
            )}
            {onSkip && (
              <Button onClick={() => onSkip(item.id)} variant="ghost" data-testid={`verify-next-btn`} className="ml-auto text-stone-600" disabled={!hasNext}>
                {skipLabel} <SkipForward className="h-4 w-4 ml-1.5" />
              </Button>
            )}
          </>
        )}
        {editing && (
          <>
            <Button
              onClick={handleEditAndVerify}
              disabled={busy || editProblems.length > 0 || !canReview}
              title={!canReview ? "Clinical reviewer only." : (editProblems.length ? `To verify: ${editProblems.join("; ")}` : "Save & verify")}
              data-testid={`save-verify-btn-${item.id}`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save & verify
            </Button>
            <Button onClick={() => setEditing(false)} variant="ghost" data-testid={`cancel-edit-btn-${item.id}`}>Cancel</Button>
          </>
        )}
        {showReject && (
          <>
            <Button onClick={handleReject} disabled={busy || !canReview} title={!canReview ? "Clinical reviewer only." : undefined} data-testid={`confirm-reject-btn-${item.id}`} className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldX className="h-4 w-4 mr-1.5" />}
              Confirm reject
            </Button>
            <Button onClick={() => setShowReject(false)} variant="ghost" data-testid={`cancel-reject-btn-${item.id}`}>Cancel</Button>
          </>
        )}
      </div>
    </article>
  );
}

function UnmappedTokenRow({ token, count, lastSeen, onAddAlias }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!target.trim()) { toast.error("Type the ingredient name or id to attach it to."); return; }
    setBusy(true);
    const ok = await onAddAlias(token, target.trim());
    setBusy(false);
    if (ok) { setOpen(false); setTarget(""); }
  };
  return (
    <li data-testid={`coverage-token-${token}`} className="px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="font-medium text-stone-900">{token}</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800 tabular-nums">
          <TrendingUp className="h-3 w-3" /> {count}
        </span>
        <span className="text-xs text-stone-400 w-24 text-right shrink-0">{fmtDate(lastSeen)}</span>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`coverage-add-alias-toggle-${token}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-stone-700 hover:bg-stone-100"
        >
          <Tag className="h-3 w-3" /> Add alias
        </button>
      </div>
      {open && (
        <div className="mt-2 flex items-center gap-2" data-testid={`coverage-add-alias-form-${token}`}>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Attach to ingredient (exact name or id)…"
            data-testid={`coverage-alias-input-${token}`}
            className="flex-1 h-9 px-3 rounded border border-stone-300 bg-white text-sm focus:border-stone-500 focus:outline-none"
          />
          <Button onClick={save} disabled={busy} data-testid={`coverage-alias-save-${token}`} className="h-9 bg-stone-900 hover:bg-stone-800 text-white text-xs">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      )}
    </li>
  );
}

function CoverageGapsPanel({ data, loading, onCreateRow, onRefresh, creatingId, onAddAlias }) {
  const pairs = data?.pairs || [];
  const meds = data?.medications || [];
  const products = data?.products || [];
  const tokens = data?.ingredient_tokens || [];
  return (
    <div data-testid="coverage-gaps-panel" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-stone-600 max-w-xl">
          What people searched that we couldn't verify yet — sorted by demand. Verify the top rows first;
          one click drafts a blank interaction row and drops you straight into its verify card.
        </p>
        <button onClick={onRefresh} data-testid="coverage-refresh" className="text-xs text-stone-500 hover:text-stone-900 px-2 py-1.5 shrink-0">Refresh</button>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : (
        <>
          <section>
            <h2 className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2 flex items-center gap-1.5">
              <FlaskConical className="h-3.5 w-3.5" /> Medication × ingredient gaps
              <span className="tabular-nums text-[10px] bg-stone-200/80 px-1.5 py-0.5 rounded-full">{pairs.length}</span>
            </h2>
            {pairs.length === 0 ? (
              <div data-testid="coverage-pairs-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-8 text-center text-sm text-stone-500">
                No pair gaps logged yet — they appear as users hit combinations we can't verify.
              </div>
            ) : (
              <div data-testid="coverage-pairs-table" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                      <tr>
                        <th className="text-left font-semibold px-4 py-3">Medication</th>
                        <th className="text-left font-semibold px-4 py-3">Ingredient</th>
                        <th className="text-left font-semibold px-4 py-3">Demand</th>
                        <th className="text-left font-semibold px-4 py-3">Last seen</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {pairs.map((p) => {
                        const key = `${p.medication_id}-${p.ingredient_id}`;
                        return (
                          <tr key={key} data-testid={`coverage-pair-${key}`} className="align-top hover:bg-stone-50/60">
                            <td className="px-4 py-3 font-medium text-stone-900">{p.medication_name}</td>
                            <td className="px-4 py-3 text-stone-700">{p.ingredient_name}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800 tabular-nums">
                                <TrendingUp className="h-3 w-3" /> {p.count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{fmtDate(p.last_seen)}</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <Button
                                onClick={() => onCreateRow(p.medication_id, p.ingredient_id)}
                                disabled={creatingId === key}
                                data-testid={`coverage-create-row-${key}`}
                                className="bg-stone-900 hover:bg-stone-800 text-white h-8 text-xs"
                              >
                                {creatingId === key ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5 mr-1" />}
                                Create interaction row
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2 flex items-center gap-1.5">
              <Pill className="h-3.5 w-3.5" /> Uncovered medication searches
              <span className="tabular-nums text-[10px] bg-stone-200/80 px-1.5 py-0.5 rounded-full">{meds.length}</span>
            </h2>
            {meds.length === 0 ? (
              <div data-testid="coverage-meds-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-8 text-center text-sm text-stone-500">
                No unknown-medication searches logged yet.
              </div>
            ) : (
              <div data-testid="coverage-meds-list" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                <ul className="divide-y divide-stone-100">
                  {meds.map((m, i) => (
                    <li key={i} data-testid={`coverage-med-${i}`} className="flex items-center gap-3 px-4 py-3">
                      <span className="font-medium text-stone-900">{m.query}</span>
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800 tabular-nums">
                        <TrendingUp className="h-3 w-3" /> {m.count}
                      </span>
                      <span className="text-xs text-stone-400 w-24 text-right shrink-0">{fmtDate(m.last_seen)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2 flex items-center gap-1.5">
              <Boxes className="h-3.5 w-3.5" /> Products not found
              <span className="tabular-nums text-[10px] bg-stone-200/80 px-1.5 py-0.5 rounded-full">{products.length}</span>
            </h2>
            {products.length === 0 ? (
              <div data-testid="coverage-products-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-8 text-center text-sm text-stone-500">
                No product-not-found searches logged yet — they appear as users look up products we don't carry.
              </div>
            ) : (
              <div data-testid="coverage-products-list" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                <ul className="divide-y divide-stone-100">
                  {products.map((p, i) => (
                    <li key={i} data-testid={`coverage-product-${i}`} className="flex items-center gap-3 px-4 py-3">
                      <span className="font-medium text-stone-900">{p.query}</span>
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800 tabular-nums">
                        <TrendingUp className="h-3 w-3" /> {p.count}
                      </span>
                      <span className="text-xs text-stone-400 w-24 text-right shrink-0">{fmtDate(p.last_seen)}</span>
                      <Link
                        to={`/admin/products?q=${encodeURIComponent(p.query || "")}`}
                        data-testid={`coverage-add-product-${i}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-stone-700 hover:bg-stone-100"
                      >
                        <PlusCircle className="h-3 w-3" /> Add product
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section>
            <h2 className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2 flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" /> Unmapped ingredients
              <span className="tabular-nums text-[10px] bg-stone-200/80 px-1.5 py-0.5 rounded-full">{tokens.length}</span>
            </h2>
            {tokens.length === 0 ? (
              <div data-testid="coverage-tokens-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-8 text-center text-sm text-stone-500">
                No unmapped ingredient tokens logged yet — they appear as users paste ingredients we can't match.
              </div>
            ) : (
              <div data-testid="coverage-tokens-list" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                <ul className="divide-y divide-stone-100">
                  {tokens.map((t, i) => (
                    <UnmappedTokenRow key={i} token={t.token} count={t.count} lastSeen={t.last_seen} onAddAlias={onAddAlias} />
                  ))}
                </ul>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function WeekPlanSidebar({ weeks, active, onPick }) {
  const w1 = weeks.find((w) => w.week === 1);
  return (
    <aside data-testid="week-plan-sidebar" className="lg:w-64 shrink-0">
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden sticky top-4">
        <div className="px-4 py-3 border-b border-stone-100">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5" /> Week plan
          </h2>
          {w1 && (
            <p className="mt-1 text-[11px] text-stone-500" data-testid="week-plan-launch-progress">
              Launch-ready at {w1.total}: <b className="text-stone-700">{w1.verified}/{w1.total}</b> verified
            </p>
          )}
        </div>
        <ul className="divide-y divide-stone-100 max-h-[70vh] overflow-y-auto">
          {weeks.map((w) => {
            const isActive = w.week === active;
            return (
              <li key={w.week}>
                <button
                  data-testid={`week-plan-item-${w.week}`}
                  onClick={() => onPick(w.week)}
                  className={`w-full text-left px-4 py-2.5 transition-colors ${isActive ? "bg-stone-900 text-white" : "hover:bg-stone-50"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold leading-tight">
                      <span className={isActive ? "text-white/50" : "text-stone-400"}>W{w.week}</span> {w.label}
                    </span>
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-white/70" : "text-stone-300"}`} />
                  </div>
                  <div className={`mt-1 flex items-center gap-1.5 text-[10px] ${isActive ? "text-white/70" : "text-stone-500"}`}>
                    <span data-testid={`week-plan-unv-${w.week}`} className="font-semibold">{w.unverified} unv</span>
                    <span>· {w.verified} ver</span>
                    <span>· {w.rejected} rej</span>
                    <span className="ml-auto opacity-70">{w.medications.length} med{w.medications.length === 1 ? "" : "s"}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}


export default function AdminVerify() {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gapDemand, setGapDemand] = useState({});
  const [sevFilter, setSevFilter] = useState("");
  const [medFilter, setMedFilter] = useState("");
  const [weekPlan, setWeekPlan] = useState([]);
  const [searchParams] = useSearchParams();
  const _deepWeek = parseInt(searchParams.get("week") || "", 10);
  const [weekFilter, setWeekFilter] = useState(
    Number.isInteger(_deepWeek) && _deepWeek >= 1 && _deepWeek <= 11 ? _deepWeek : 1,
  );   // default opens Week 1
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Deep-link support: the weekly coverage digest email links here with
  // ?tab=coverage_gaps so admins land straight on the Coverage gaps tab.
  const deepLinkTab = searchParams.get("tab") || searchParams.get("view");
  const [view, setView] = useState(
    deepLinkTab === "coverage_gaps" ? "coverage_gaps" : "unverified",
  );   // "unverified" | "verified" | "source_pending" | "coverage_gaps"
  const [focusId, setFocusId] = useState(null);       // single-item review mode
  const [gapsData, setGapsData] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [creatingId, setCreatingId] = useState(null);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchVerificationQueue({
        limit: view === "verified" ? 200 : 500,
        severity: sevFilter || undefined,
        medicationId: medFilter || undefined,
        week: view === "coverage_gaps" ? undefined : (weekFilter || undefined),
        verified: view === "verified" ? true : undefined,
        sourcePending: view === "source_pending" ? true : undefined,
      });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }, [sevFilter, medFilter, view, weekFilter]);

  useEffect(() => { reload(); }, [reload]);

  const reloadWeekPlan = useCallback(async () => {
    try {
      const d = await fetchVerificationWeekPlan();
      setWeekPlan(d?.weeks || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { reloadWeekPlan(); }, [reloadWeekPlan]);

  useEffect(() => {
    let cancelled = false;
    fetchGapDemand()
      .then((d) => { if (!cancelled) setGapDemand(d?.pairs || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadGaps = useCallback(async () => {
    setGapsLoading(true);
    try {
      const d = await fetchCoverageGaps();
      setGapsData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load coverage gaps");
    } finally { setGapsLoading(false); }
  }, []);

  useEffect(() => {
    if (view === "coverage_gaps" && gapsData === null) loadGaps();
  }, [view, gapsData, loadGaps]);

  const handleCreateRow = async (medId, ingId) => {
    const key = `${medId}-${ingId}`;
    setCreatingId(key);
    try {
      const res = await createCoverageRow(medId, ingId);
      toast.success(res.created ? "Draft row created — add severity + source" : "Opening the existing row for this pair");
      setGapsData(null);           // force a refresh next time the tab opens
      setView("unverified");
      setFocusId(res.id);           // focus resolves once the queue reloads
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the row");
    } finally { setCreatingId(null); }
  };

  const handleAddAlias = async (token, ingredient) => {
    try {
      const res = await addIngredientAlias(token, ingredient);
      toast.success(`"${token}" added as an alias of ${res.ingredient_name || res.ingredient_id}`);
      loadGaps();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add alias");
      return false;
    }
  };

  const items = data?.items || [];
  const summary = data?.summary || { pending: 0, verified: 0, verified_this_week: 0, by_severity: {}, source_pending: 0 };
  const medOptions = data?.medication_options || [];
  const allowedDomains = data?.allowed_source_domains || [];
  const activeWeek = weekPlan.find((w) => w.week === weekFilter) || null;
  const bulkEligible = view === "unverified" || view === "source_pending";

  const pickWeek = (w) => { setWeekFilter(w); setFocusId(null); clearSelection(); };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () => setSelectedIds((prev) => (
    prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))
  ));

  const doBulkReject = async (reason) => {
    if (!canReview) { toast.error("Clinical reviewer only."); return; }
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await rejectInteractionBulk(ids, reason);
      toast.success(`Rejected ${res.count} row${res.count === 1 ? "" : "s"}`);
      clearSelection();
      reload();
      reloadWeekPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk reject failed");
    } finally { setBulkBusy(false); }
  };

  const advanceFrom = (id) => {
    const idx = items.findIndex((i) => i.id === id);
    const next = items[idx + 1];
    return next ? next.id : null;
  };

  const onActed = (id) => {
    const nextId = advanceFrom(id);
    setData((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== id) } : prev);
    reload();
    reloadWeekPlan();
    setFocusId(nextId);
  };

  const onSkip = (id) => {
    const nextId = advanceFrom(id);
    setFocusId(nextId);
  };

  const focusItem = focusId ? items.find((i) => i.id === focusId) : null;
  const focusHasNext = focusId ? advanceFrom(focusId) != null : false;
  const rowsClickable = view !== "verified";

  return (
    <main data-testid="admin-verify-page" className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12">
      <div className="mx-auto max-w-7xl">
        <AdminHeader />

        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Verification Queue</h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              Every interaction, highest clinical severity first. A row can only be verified once it
              has a checkable source (allowed https link or textbook + page) and an evidence level.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wider text-stone-600">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        </header>

        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <StatTile label="Unverified" value={summary.pending} tone="amber" testid="verify-summary-unverified" />
          <StatTile label="Source pending" value={summary.source_pending} tone="red" testid="verify-summary-source-pending" />
          <StatTile label="Verified this week" value={summary.verified_this_week} tone="green" testid="verify-summary-week" />
          <StatTile label="Total verified" value={summary.verified} tone="stone" testid="verify-summary-total" />
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 mb-4 p-1 bg-stone-100 rounded-full w-fit">
          {[
            { id: "unverified",     label: "Unverified",     count: summary.pending },
            { id: "verified",       label: "Verified",       count: summary.verified },
            { id: "source_pending", label: "Source pending", count: summary.source_pending },
            { id: "coverage_gaps",  label: "Coverage gaps",  count: gapsData ? (gapsData.pair_count + gapsData.medication_count + (gapsData.product_count || 0) + (gapsData.ingredient_token_count || 0)) : undefined },
          ].map((opt) => (
            <button key={opt.id} onClick={() => { setView(opt.id); setFocusId(null); }} data-testid={`verify-view-${opt.id}`}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${view === opt.id ? "bg-stone-900 text-white shadow-sm" : "text-stone-600 hover:text-stone-900"}`}>
              {opt.label}
              <span className={`tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${view === opt.id ? "bg-white/20" : "bg-stone-200/80"}`}>{opt.count ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {view !== "coverage_gaps" && (
            <WeekPlanSidebar weeks={weekPlan} active={weekFilter} onPick={pickWeek} />
          )}
          <div className="flex-1 min-w-0">

        {/* Coverage gaps view */}
        {view === "coverage_gaps" && (
          <CoverageGapsPanel
            data={gapsData}
            loading={gapsLoading}
            onCreateRow={handleCreateRow}
            onRefresh={loadGaps}
            creatingId={creatingId}
            onAddAlias={handleAddAlias}
          />
        )}

        {/* Queue views */}
        {view !== "coverage_gaps" && (
        <>
        {activeWeek && (
          <div data-testid="week-header" className="mb-4 rounded-xl border border-stone-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-stone-900" data-testid="week-header-title">
                Week {activeWeek.week}: {activeWeek.label}
              </h3>
              <div className="text-[11px] text-stone-500" data-testid="week-header-counts">
                {activeWeek.unverified} unverified · {activeWeek.verified} verified · {activeWeek.rejected} rejected
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1" data-testid="week-header-meds">
              {activeWeek.medications.map((m) => (
                <span key={m.id} className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-600">
                  {m.name}{m.unverified ? ` · ${m.unverified}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bulk reject bar (Reject only — never bulk verify) */}
        {bulkEligible && selectedIds.size > 0 && (
          <div data-testid="bulk-reject-bar" className="mb-3 sticky top-2 z-10 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 flex items-center gap-2 flex-wrap shadow-sm">
            <Trash2 className="h-4 w-4 text-red-600" />
            <span className="text-xs font-semibold text-red-800" data-testid="bulk-selected-count">{selectedIds.size} selected</span>
            <span className="text-[11px] text-red-600">Reject with:</span>
            {REJECT_PRESETS.map((p) => (
              <button
                key={p}
                disabled={bulkBusy || !canReview}
                onClick={() => doBulkReject(p)}
                data-testid={`bulk-reject-preset-${p.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                className="rounded-full border border-red-300 bg-white px-2.5 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                {p}
              </button>
            ))}
            {!canReview && <span className="text-[10px] text-amber-700">Clinical reviewer only</span>}
            <button onClick={clearSelection} data-testid="bulk-clear" className="ml-auto text-[11px] text-stone-500 hover:text-stone-800">Clear</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-stone-500 inline-flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Severity:</span>
          <select value={sevFilter} onChange={(e) => { setSevFilter(e.target.value); setFocusId(null); }} data-testid="verify-filter-severity"
            className="text-xs h-8 px-2 rounded-full border border-stone-300 bg-white text-stone-700">
            <option value="">All severities</option>
            {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{SEV_LABEL[s]}</option>)}
          </select>
          <select value={medFilter} onChange={(e) => { setMedFilter(e.target.value); setFocusId(null); }} data-testid="verify-filter-medication"
            className="text-xs h-8 px-2 rounded-full border border-stone-300 bg-white text-stone-700 max-w-[220px]">
            <option value="">All medications</option>
            {medOptions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button onClick={reload} data-testid="verify-refresh" className="ml-auto text-xs text-stone-500 hover:text-stone-900 px-2 py-1.5">Refresh</button>
        </div>

        {/* Focus mode (single-item review) */}
        {rowsClickable && focusItem && (
          <div className="mb-4" data-testid="verify-focus-panel">
            <button onClick={() => setFocusId(null)} data-testid="verify-focus-back" className="mb-3 inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900">
              <ArrowLeft className="h-3 w-3" /> Back to list
            </button>
            <VerifyCard item={focusItem} allowedDomains={allowedDomains} waiting={gapDemand[`${focusItem.medication_id}:${focusItem.ingredient_id}`] || 0} skipLabel={view === "source_pending" ? "Next source-pending" : "Next unverified"} onVerified={onActed} onRejected={onActed} onSkip={onSkip} hasNext={focusHasNext} />
          </div>
        )}

        {/* List / table */}
        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div data-testid="verify-empty-state" className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center">
            <ShieldCheck className="h-10 w-10 text-emerald-400 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-stone-700">
              {view === "verified" ? "No verified rows match this filter." : view === "source_pending" ? "Every active row has a checkable source." : "All clear."}
            </p>
            <p className="mt-1 text-xs text-stone-500">
              {view === "verified" ? "Try clearing the filters." : view === "source_pending" ? "No rows are waiting for a source URL." : "No interactions are awaiting verification right now."}
            </p>
          </div>
        ) : focusItem && rowsClickable ? null : (
          <div data-testid={view === "verified" ? "verify-verified-table" : view === "source_pending" ? "verify-source-pending-table" : "verify-unverified-table"} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                  <tr>
                    {bulkEligible && (
                      <th className="px-3 py-3 w-9">
                        <input
                          type="checkbox"
                          data-testid="verify-select-all"
                          aria-label="Select all rows"
                          checked={items.length > 0 && selectedIds.size === items.length}
                          ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < items.length; }}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 cursor-pointer accent-red-600"
                        />
                      </th>
                    )}
                    <th className="text-left font-semibold px-4 py-3">Medication</th>
                    <th className="text-left font-semibold px-4 py-3">Ingredient</th>
                    <th className="text-left font-semibold px-4 py-3">Severity</th>
                    <th className="text-left font-semibold px-4 py-3">Existing source</th>
                    <th className="text-left font-semibold px-4 py-3">Created</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    {rowsClickable && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {items.map((r) => {
                    const medName = r.medication?.generic_name || "";
                    const src = r.source_primary_url
                      ? <a className="text-blue-600 hover:underline inline-flex items-center gap-1" href={r.source_primary_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{r.source_primary || "link"}<ExternalLink className="h-3 w-3" /></a>
                      : (r.source_primary || <span className="text-stone-400 italic">none</span>);
                    return (
                      <tr key={r.id} data-testid={`${view}-row-${r.id}`}
                        onClick={rowsClickable ? () => setFocusId(r.id) : undefined}
                        className={`align-top ${selectedIds.has(r.id) ? "bg-red-50/60" : ""} ${rowsClickable ? "cursor-pointer hover:bg-amber-50/50" : "hover:bg-stone-50/60"}`}>
                        {bulkEligible && (
                          <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              data-testid={`verify-row-checkbox-${r.id}`}
                              aria-label="Select row"
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleSelect(r.id)}
                              className="h-4 w-4 cursor-pointer accent-red-600"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 font-medium text-stone-900 whitespace-nowrap">
                          {medName || r.medication_id?.slice(0, 8) || "—"}
                          {r.medication && (
                            <DailyMedChip
                              setid={r.medication?.label_setid}
                              testid={`dailymed-link-${r.id}`}
                              small
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-stone-700 whitespace-nowrap">
                          {r.ingredient?.name || r.ingredient_id?.slice(0, 8) || "—"}
                          {(gapDemand[`${r.medication_id}:${r.ingredient_id}`] || 0) > 0 && (
                            <span
                              data-testid={`gap-waiting-row-${r.id}`}
                              className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[9px] font-bold text-amber-900 align-middle"
                            >
                              <Bell className="h-2.5 w-2.5" /> {gapDemand[`${r.medication_id}:${r.ingredient_id}`]} waiting
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3"><SevPill severity={r.severity} /></td>
                        <td className="px-4 py-3 text-stone-600 text-xs max-w-[220px] truncate">{src}</td>
                        <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.verified ? (r.med_launched ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-sky-50 border-sky-200 text-sky-800") : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                            {r.verified ? (r.med_launched ? "Verified — live" : "Verified — awaiting launch") : "Unverified"}
                          </span>
                        </td>
                        {rowsClickable && (
                          <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-stone-400 inline" /></td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}
          </div>
        </div>
      </div>
    </main>
  );
}
